import { useUserStore } from '@/src/common/store/useUserStore'
import { PlatformStatsigProvider, useStatsigClient } from '@/src/services/Statsig/PlatformStatsig'
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { appVersion } from '@/src/common/utils/appVersion'
import type { StatsigClientEventName, StatsigUser } from '@statsig/client-core'
import { isProduction } from '@/src/common/environment'
import { Platform } from 'react-native'

export function useStatsigUser() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { userInformation } = useUserStore()
  const { id: userID, email, sourcePartnerName: nullableSourcePartnerName } = userInformation || {}
  const sourcePartnerName = nullableSourcePartnerName ?? undefined
  return useMemo(
    () =>
      ({
        userID,
        email,
        appVersion,
        custom: { sourcePartnerName, platform: Platform.OS },
      }) satisfies StatsigUser,
    [userID, email, sourcePartnerName],
  )
}

export function StatsigProvider(props: Required<PropsWithChildren<{ statsigUser: StatsigUser }>>) {
  return (
    <PlatformStatsigProvider
      sdkKey={process.env.EXPO_PUBLIC_STATSIG_API_KEY ?? ''}
      user={props.statsigUser}
      options={{
        environment: {
          tier: isProduction ? 'production' : 'staging',
        },
        networkConfig: {
          api: process.env.EXPO_PUBLIC_STATSIG_PROXY_URL ?? '',
        },
      }}
      {...props}
    />
  )
}

export function useStatsig(statsigUser: StatsigUser) {
  const { client } = useStatsigClient()
  useEffect(() => {
    void client.updateUserAsync(statsigUser)
  }, [client, statsigUser])
}

export function useStatsigLoadingStatus() {
  const { client } = useStatsigClient()
  const [loadingStatus, setLoadingStatus] = useState(client.loadingStatus)

  useEffect(() => {
    const values_updated = 'values_updated' satisfies StatsigClientEventName
    const listener: Parameters<typeof client.on<typeof values_updated>>[1] = ({ status }) =>
      setLoadingStatus(status)
    client.on(values_updated, listener)
    return () => client.off(values_updated, listener)
  }, [client])

  return loadingStatus
}
